import { useCallback, useRef, useState } from 'react';

export interface ElementSize {
	width: number;
	height: number;
}

/**
 * Tracks an element's rendered size via ResizeObserver. Returns a ref callback to put on the
 * element and its current size, `{ width: 0, height: 0 }` until it has been measured.
 */
const useElementSize = (): [(element: HTMLElement | null) => void, ElementSize] => {
	const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
	const observerRef = useRef<ResizeObserver | null>(null);

	const measureRef = useCallback((element: HTMLElement | null) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
		if (!element) return;
		const measure = () => setSize({ width: element.offsetWidth, height: element.offsetHeight });
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		observerRef.current = observer;
	}, []);

	return [measureRef, size];
};

export default useElementSize;
